import { useEffect, useRef, useState } from "react";
import { ImageOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { resolvePhotoTileSource } from "@/lib/photoTileSource";
import { PHOTO_TILE_TIMEOUT_MS, recordTileEvent, schedulePhotoTile } from "@/lib/photoTileRuntime";
import { usePhotoTileVisibility } from "@/hooks/usePhotoTileVisibility";

interface PhotoTileProps {
  source: string;
  alt: string;
  className?: string;
  bucket?: string;
  onClick?: () => void;
  /** Preserve existing local-source recovery, never automatic transformed-to-original fallback. */
  onSourceError?: () => void;
  /**
   * Fill a positioned (relative) aspect-ratio parent by absolute insets.
   * WebKit/WKWebView treats `height:100%` inside an aspect-ratio box as
   * indefinite, so the image laid out at its natural height and object-fit
   * placed the picture below the clipped tile: a decoded image showing only the
   * grey background (iPhone Completed Job → View Photos, 1.81/1.82 build 189).
   */
  fill?: boolean;
}

/** Inline so no caller class (h-full, relative) can override it. */
export const PHOTO_TILE_FILL_STYLE = { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, width: "100%", height: "100%" } as const;

export function PhotoTile({ source, alt, className, bucket = "appraisal-photos", onClick, onSourceError, fill = false }: PhotoTileProps) {
  const { ref, visible } = usePhotoTileVisibility();
  const imageRef = useRef<HTMLImageElement>(null);
  const finishRef = useRef<(outcome: "failed" | "timed_out" | "decoded" | "decode_failed") => void>(() => {});
  const loadRef = useRef<() => void>(() => {});
  const [src, setSrc] = useState<string | undefined>();
  const [status, setStatus] = useState("waiting");
  const [retry, setRetry] = useState(0);
  const [useOriginal, setUseOriginal] = useState(false);
  // Permanent application-wide correction; source eligibility is not a permission check.
  const resolved = resolvePhotoTileSource(source, bucket, true);
  const target = useOriginal ? resolved.original : resolved.preview;
  const errorRef = useRef(onSourceError);
  errorRef.current = onSourceError;

  useEffect(() => { recordTileEvent("mounted"); return () => recordTileEvent("unmounted"); }, []);
  useEffect(() => { setUseOriginal(false); }, [source, bucket]);
  useEffect(() => {
    setSrc(undefined);
    setStatus("waiting");
    if (!visible) return;
    if (!target) { setStatus("failed"); return; }
    recordTileEvent("visible");
    let disposed = false;
    let settled = false;
    let loaded = false;
    let release: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let startedAt = 0;
    const finish = (outcome: "failed" | "timed_out" | "decoded" | "decode_failed") => {
      if (disposed || settled) return;
      settled = true;
      clearTimeout(timer);
      recordTileEvent(outcome, performance.now() - startedAt);
      const failed = outcome === "failed" || outcome === "timed_out";
      if (failed) {
        imageRef.current?.removeAttribute("src"); // Cancel stalled image before returning its permit.
        setSrc(undefined);
      }
      setStatus(outcome === "decoded" ? "ready" : outcome);
      release?.();
      if (outcome === "failed" && (!resolved.transformed || useOriginal)) errorRef.current?.();
    };
    finishRef.current = finish;
    loadRef.current = () => {
      if (disposed || settled || loaded) return;
      loaded = true;
      recordTileEvent("loaded", performance.now() - startedAt);
      setStatus("loaded");
      const node = imageRef.current;
      if (!node || typeof node.decode !== "function") { finish("decode_failed"); return; }
      node.decode().then(() => finish("decoded"), () => finish("decode_failed"));
    };
    const cancelWaiting = schedulePhotoTile((done) => {
      release = done;
      if (disposed) { done(); return; }
      startedAt = performance.now();
      recordTileEvent("requested");
      setStatus("requested");
      timer = setTimeout(() => finish("timed_out"), PHOTO_TILE_TIMEOUT_MS);
      setSrc(target);
    });
    return () => {
      disposed = true;
      cancelWaiting();
      clearTimeout(timer);
      imageRef.current?.removeAttribute("src");
      if (release && !settled) recordTileEvent("cancelled");
      release?.();
    };
  }, [target, visible, retry, resolved.transformed, useOriginal]);

  useEffect(() => {
    const node = imageRef.current;
    if (src && node?.complete && node.naturalWidth > 0) loadRef.current();
  }, [src]);

  const unavailable = status === "failed" || status === "timed_out" || status === "decode_failed";
  return (
    <div ref={ref} className={cn("relative bg-muted", className)} style={fill ? PHOTO_TILE_FILL_STYLE : undefined} data-photo-tile-state={status} data-photo-tile-fill={fill ? "true" : undefined}>
      {src && <img ref={imageRef} src={src} alt={alt} className={cn("w-full h-full", className)} style={fill ? PHOTO_TILE_FILL_STYLE : undefined} loading="eager" decoding="async" onLoad={() => loadRef.current()} onError={() => finishRef.current("failed")} onClick={onClick} />}
      {unavailable && <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground" role="img" aria-label={`${alt} (preview unavailable)`}>
        <ImageOff className="h-5 w-5" aria-hidden="true" />
        <span className="text-[10px] text-center">Preview unavailable</span>
        <Button asChild variant="ghost" size="icon" aria-label={`Retry ${alt} preview`} title="Retry preview" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRetry((n) => n + 1); }}><span role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setRetry((n) => n + 1); } }}><RefreshCw /></span></Button>
        {resolved.transformed && !useOriginal && <Button asChild variant="link" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setUseOriginal(true); }}><span role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setUseOriginal(true); } }}>Load original</span></Button>}
      </div>}
    </div>
  );
}